import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import useAuthAdmin from "../useAuthAdmin";

const useEmployeesManagement = () => {
  const { adminSession, logout } = useAuthAdmin();
  const [employees, setEmployees] = useState(null);
  const [singleEmployee, setSingleEmployee] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const navigate = useNavigate();

  const fetchEmployees = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/employees/basic-information`,
        {
          headers: {
            Authorization: `Bearer ${adminSession}`,
          },
        }
      );
     
      setEmployees(response.data.data || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired. Please log in again.", {
          toastId: "employeesFetchError",
        });
        logout(() => navigate("/login"));
      } else {
        toast.error("Failed to fetch employee data.", {
          toastId: "employeesFetchError",
        });
      }
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [adminSession, logout, navigate]);

  const fetchSingleEmployee = useCallback(
    async (userId) => {
      setLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/v1/employees/all-information/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${adminSession}`,
            },
          }
        );
        setSingleEmployee(response.data.data || null);
      } catch (err) {
        if (err?.response?.status === 401) {
          toast.error("Session expired. Please log in again.", {
            toastId: "singleEmployeeFetchError",
          });
          logout(() => navigate("/login"));
        } else {
          toast.error("Failed to fetch employee data.", {
            toastId: "singleEmployeeFetchError",
          });
        }
        setError(err);
      } finally {
        setLoading(false);
      }
    },
    [adminSession, logout, navigate]
  );

  const addEmployee = async (formData, onSuccess) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/employees/add-employee-with-credentials`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${adminSession}`,
          },
        }
      );

      if (response.data.status === "201") {
        toast.success("Employee added successfully!");
        if (onSuccess) onSuccess(response.data.data);
      } else {
        toast.error("Failed to add employee.");
      }
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired. Please Login again", {
          toastId: "logout",
        });
        logout(() => navigate("/login"));
      } else if (err?.response?.status === 400 && err?.response?.data?.message) {
        // Show backend validation/message (e.g., duplicate email)
        toast.error(err.response.data.message, { toastId: "addEmployeeError" });
        setError(err);
      } else {
        toast.error("Failed to add employee. Please try again.");
        setError(err);
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleApproval = async (userId, status, onSuccess) => {
    setLoading(true);
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/employees/toggle-approval/${userId}`,
        { status }, // Sending the desired status
        {
          headers: {
            Authorization: `Bearer ${adminSession}`,
          },
        }
      );

      if (response.data.status === "200") {
        setEmployees((prev) =>
          prev.map((emp) =>
            emp.userId === userId ? { ...emp, isApproved: status } : emp
          )
        );

        if (onSuccess) onSuccess();
      } else {
        toast.error("Failed to toggle approval status.", {
          toastId: "toggleApprovalError",
        });
      }
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired. Please Login again", {
          toastId: "logout",
        });
        logout(() => navigate("/login"));
      } else {
        toast.error("Failed to toggle approval status.", {
        toastId: "toggleApprovalError",
      });
      setError(err);}
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (employees === null) fetchEmployees();
  }, [fetchEmployees, employees]);

  return {
    employees,
    singleEmployee,
    loading,
    error,
    fetchEmployees,
    fetchSingleEmployee,
    toggleApproval,
    addEmployee,
  };
};

export default useEmployeesManagement;
